import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { pick } from 'lodash-es';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spin } from 'antd';

import {
  shipmentTemplateFormToInput,
  shipmentTemplateGraphQLToForm,
} from '../../../app/data/shipmentTemplateConversions';
import {
  CREATE_SHIPMENT_TEMPLATE_MUTATION,
  DELETE_SHIPMENT_TEMPLATE_MUTATION,
  EDIT_SHIPMENT_TEMPLATE_MUTATION,
  JOB_TEMPLATE_QUERY,
} from '../../../app/graphql/jobTemplateQueries';
import {
  deleteQueryFromCache,
  extractGraphqlEntity,
} from '../../../common/utils/graphqlUtils';
import { useDynamicOriginalValueDependency } from '../../../components/forms/dynamic/dynamicFormDependencies';
import { TwoLines } from '../../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import TemplatesFormDrawer from '../../../components/popups/templates/TemplatesFormDrawer';
import { SavedShipmentProvider } from '../newShipment/SavedShipmentProvider';
import useNewShipmentFormState from '../newShipment/useNewShipmentFormState';
import ShipmentTemplateFormContent from './ShipmentTemplateFormContent';
import { useShipmentTemplateDrawerStyles } from './ShipmentTemplateDrawer.styles';
import { mergeLoadedTemplateIntoDrawerData } from './shipmentTemplateUtils';

function useUpdateQueries() {
  const accountNumber = useDynamicOriginalValueDependency('accountNumber');
  return cache =>
    deleteQueryFromCache(
      cache,
      'customerShipmentTemplates',
      ({ args }) => args.accountNumber === accountNumber
    );
}

function useCreateTemplate() {
  const updateQueries = useUpdateQueries();
  const [mutateSave] = useMutation(CREATE_SHIPMENT_TEMPLATE_MUTATION, {
    update: updateQueries,
  });

  return async function create(input) {
    const { data } = await mutateSave({ variables: { input } });
    return extractGraphqlEntity(data);
  };
}

function useEditTemplate() {
  const updateQueries = useUpdateQueries();
  const [mutateEdit] = useMutation(EDIT_SHIPMENT_TEMPLATE_MUTATION, {
    update: updateQueries,
  });
  return async function edit(id, input) {
    const { data } = await mutateEdit({ variables: { id, input } });
    return extractGraphqlEntity(data);
  };
}

function useRemoveTemplate() {
  const updateQueries = useUpdateQueries();
  const [mutateRemove] = useMutation(DELETE_SHIPMENT_TEMPLATE_MUTATION, {
    update: updateQueries,
  });
  return async function remove(id) {
    await mutateRemove({ variables: { id } });
  };
}

export function ShipmentTemplateDrawer({
  data,
  setData,
  onClose,
  afterSave,
  afterDelete,
  values: shipmentValues,
  open,
  readOnly,
  renderContentOnly,
}) {
  const { forms, refreshValues, account, setAccount, setUnitSystem, values } =
    useNewShipmentFormState();

  const accountNumber = useDynamicOriginalValueDependency('accountNumber');
  const { styles } = useShipmentTemplateDrawerStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const templateIdToFetch = data?.editing?.id || data?.adding?.id;

  const { data: templateData, loading } = useQuery(JOB_TEMPLATE_QUERY, {
    variables: { id: templateIdToFetch },
    skip: !templateIdToFetch,
    fetchPolicy: 'network-only',
  });

  const loadedTemplate = useMemo(() => {
    if (templateData) {
      const entity = extractGraphqlEntity(templateData);
      if (entity) {
        return shipmentTemplateGraphQLToForm(entity);
      }
    }
    return null;
  }, [templateData]);

  const getInput = vals =>
    shipmentTemplateFormToInput({
      ...values,
      name: data?.editing?.name,
      accountNumber,
      ...vals,
    });

  const createTemplate = useCreateTemplate();
  const editTemplate = useEditTemplate();
  const removeTemplate = useRemoveTemplate();

  const getEditInitialValues = entry => pick(entry, ['name']);

  const getCreateInitialValues = () => {
    if (data?.adding && loadedTemplate) {
      return {
        ...loadedTemplate,
        name: data.adding.name,
      };
    }
    return {};
  };

  const formContentInitialValues = loadedTemplate || shipmentValues;

  if (loading && templateIdToFetch) {
    return (
      <div className={classNames(layoutStyles.flex1, 'flex-center')}>
        <Spin />
      </div>
    );
  }

  return (
    <SavedShipmentProvider enabled={false}>
      <TemplatesFormDrawer
        data={data}
        setData={setData}
        onClose={onClose}
        readOnly={readOnly}
        titleId="shipmentTemplates.drawerTitle"
        onSave={async (id, formValues) => {
          const input = getInput(formValues);

          if (!id && data?.adding?.id) {
            const originalName = data.adding.name;
            if (input.name === originalName) {
              input.name = `${input.name} copy`;
            }
          }

          const response = id
            ? await editTemplate(id, input)
            : await createTemplate(input);

          onClose();
          afterSave({ ...input, id: response.id });
        }}
        allowSaveAs
        onDelete={async id => {
          await removeTemplate(id);
          onClose();
          afterDelete({ id });
        }}
        getEditInitialValues={getEditInitialValues}
        getCreateInitialValues={getCreateInitialValues}
        formClassName="ShipmentTemplateForm"
        formName="shipmentTemplateEntry"
        formComponent={ShipmentTemplateFormContent}
        formComponentProps={{
          initialValues: formContentInitialValues,
          data: mergeLoadedTemplateIntoDrawerData(data, loadedTemplate),
          forms,
          refreshValues,
          account,
          setAccount,
          setUnitSystem,
          values,
        }}
        createSuccessMessageId="shipmentTemplates.messages.save.success"
        editSuccessMessageId="shipmentTemplates.edit.success"
        removeDialogClassName={classNames(
          styles.deleteShipmentTemplateDialog,
          'size-sm'
        )}
        removeText={
          <>
            <div
              className={`TemplateOperationWarning-Name ${styles.templateOperationWarningName}`}
            >
              {data?.editing?.name}
            </div>
            <TwoLines
              className={classNames(
                'size-6',
                styles.deleteShipmentTemplateDivider
              )}
            />
            <div className={styles.templateOperationWarningText}>
              <FormattedMessage id="shipmentTemplates.delete.dialog.text" />
            </div>
          </>
        }
        removeSuccessMessageId="shipmentTemplates.delete.success"
        renderContentOnly={renderContentOnly}
        open={open}
        data-subject="shipment-templates"
        data-role="drawer"
      />
    </SavedShipmentProvider>
  );
}
